import React, { useState } from 'react';
import axios from 'axios';
import { rate as perHour } from '../lib/money';
import { useConfirm } from './ConfirmDialog';
import './WorkerToggles.css';

/**
 * Whether this person fixes tickets, and whether they invoice for it.
 *
 * Gordon's model, 5 October: somebody with an @ncmb.eu address gets a tickbox
 * saying whether they have work abilities, and only then do they appear in the
 * contractors list at all; and a SECOND tickbox for invoicing, because an
 * employee does not invoice while an external person who happens to have an
 * internal mailbox does.
 *
 * The second box is deliberately hidden until the first is ticked. Asked of
 * everybody it reads as a question about staff expenses, which it is not, and
 * the answer for somebody who does not fix tickets is neither yes nor no.
 *
 * THE RATE LABEL CHANGES WITH THE SECOND BOX, and that is the point of this
 * component rather than two loose checkboxes. The same NUMERIC column is a
 * charge-out rate for somebody who invoices and an internal cost for somebody
 * who does not - and those are not the same number for the same person. A
 * field labelled "Hourly rate" collects whichever the person filling it in
 * happened to think of, and nothing downstream can tell which it got.
 */
const WorkerToggles = ({ user, onSaved }) => {
  const works = Boolean(user.worker_id) && user.worker_status !== 'inactive';
  const invoices = user.worker_invoices === true;

  const [busy, setBusy] = useState(false);
  const [confirm, confirmDialog] = useConfirm();
  const [editingRate, setEditingRate] = useState(false);
  const [draftRate, setDraftRate] = useState('');
  const [error, setError] = useState(null);
  // What the server said on saving, when it said anything - shown under the
  // boxes in its own words. It was a window.alert().
  const [message, setMessage] = useState(null);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  const save = async (body) => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const res = await axios.put(`${API_URL}/users/${user.id}/worker`, body);
      if (res.data?.message) setMessage(res.data.message);
      if (onSaved) await onSaved();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save that');
    } finally {
      setBusy(false);
    }
  };

  const toggleWorks = async () => {
    if (works) {
      // Said out loud, because the record is kept and that surprises people
      // who expect a tickbox to undo itself completely.
      if (!(await confirm({
        title: `Stop ${user.display_name || user.email} being assigned tickets?`,
        message: 'Their work record is kept, so hours and invoices already recorded '
          + 'against it stay intact. Tick the box again to bring them back.',
        confirmLabel: 'Stop'
      }))) return undefined;
      return save({ works: false });
    }
    return save({ works: true, invoices: false });
  };

  const rateLabel = invoices ? 'Charge-out rate' : 'Internal cost';

  if (!works) {
    return (
      <div className="sw">
        <label className="sw-check">
          <input type="checkbox" checked={false} disabled={busy} onChange={toggleWorks} />
          <span>Fixes tickets</span>
        </label>
        {user.worker_id && (
          <span className="sw-note">Work record kept, currently inactive</span>
        )}
        {message && <span className="sw-note" role="status">{message}</span>}
        {error && <span className="sw-error">{error}</span>}
        {confirmDialog}
      </div>
    );
  }

  return (
    <div className="sw">
      <label className="sw-check">
        <input type="checkbox" checked disabled={busy} onChange={toggleWorks} />
        <span>Fixes tickets</span>
      </label>

      <label className="sw-check sw-sub">
        <input
          type="checkbox"
          checked={invoices}
          disabled={busy}
          onChange={() => save({ works: true, invoices: !invoices })}
        />
        <span>Invoices for it</span>
      </label>

      <div className="sw-rate">
        <span className="sw-rate-label">{rateLabel}</span>
        {editingRate ? (
          <>
            <input
              type="number"
              step="0.01"
              min="0"
              className="sw-rate-input"
              aria-label={`${rateLabel} for ${user.display_name || user.email}`}
              value={draftRate}
              autoFocus
              onChange={(e) => setDraftRate(e.target.value)}
            />
            <button
              type="button"
              className="btn btn-small btn-primary"
              disabled={busy}
              onClick={async () => {
                await save({ works: true, invoices, hourly_rate: draftRate });
                setEditingRate(false);
              }}
            >
              Save
            </button>
            <button
              type="button"
              className="btn btn-small btn-secondary"
              onClick={() => setEditingRate(false)}
            >
              Cancel
            </button>
          </>
        ) : (
          <button
            type="button"
            className="sw-rate-value"
            onClick={() => {
              setDraftRate(user.worker_rate == null ? '' : String(Number(user.worker_rate)));
              setEditingRate(true);
            }}
          >
            {perHour(user.worker_rate, { dash: 'Not set' })}
          </button>
        )}
      </div>

      {/* The consequence of the second box, in the words of the screen it
          changes. Without it the difference between the two states is a
          relabelled field, which nobody reads as "their work never reaches
          billing". */}
      <span className="sw-note">
        {invoices
          ? 'Approved hours go to Billing as usual'
          : 'Approved hours are costed, never invoiced'}
      </span>

      {message && <span className="sw-note" role="status">{message}</span>}
      {error && <span className="sw-error">{error}</span>}
      {confirmDialog}
    </div>
  );
};

export default WorkerToggles;
